import { toast } from "react-toastify";
import Swal from "sweetalert2";
import {
	login,
	loginWithHybridToken,
	loginWithToken,
	signOut,
	validate2FA,
} from "../services/identity.service";
import { getProfile } from "../services/player.service";
import {
	CLEAR_USER_PROFILE,
	LOGIN_FAIL,
	LOGIN_SUCCESS,
	LOGOUT,
	UPDATE_USER_PROFILE,
} from "./types";
import {
	seamlessSignIn,
	seamlessSignOut,
} from "../services/seamlessIdentity.service";
import { seamlessGetProfile } from "../services/seamless.service";
import {
	hybridAddBankAccount,
	hybridGetWithdrawBankSetting,
	hybridGetProfile,
	hybridRegisterRequestOtp,
	hybridVerifyPhoneNumber,
} from "../services/hybridPlayer.service";
import { modeTypes } from "../constants/mode";
import config from "../config";
import { clearLocalStorageExceptRemember } from "../utils";

// Call the validate2FA API for MM1 admins and for email/phone login
// (email/phone may resolve to an admin account).
export const adminLoginValidate2FA = async (data: any) => {
	const username = data.player_username?.trim().toLowerCase();
	const isMM1Admin = username?.startsWith("mm1");
	const isEmailOrPhoneLogin =
		data.hybrid_login === true ||
		data.hybrid_email_login === true ||
		String(data.hybrid_login).toLowerCase() === "true" ||
		String(data.hybrid_email_login).toLowerCase() === "true";

	if (!isMM1Admin && !isEmailOrPhoneLogin) {
		// Non-admin username path → always direct login
		return { otpRequired: false };
	}

	try {
		const result = await validate2FA(data);
		console.log("validate2FA result:", result);

		if (result?.status === false) {
			return {
				otpRequired: false,
				blocked: true,
				message: result.message || "Please use other methods to login",
			};
		}

		return {
			otpRequired: result.type === "ENTER_OTP",
		};
	} catch (error) {
		console.error("Error in adminLoginValidate2FA:", error);
		return { otpRequired: false };
	}
};

const clearPendingLoginSession = () => {
	sessionStorage.removeItem("user_token");
	localStorage.removeItem("user_token");
	localStorage.removeItem("currentMode");
};

const dismissLoginBootstrapModals = () => {
	const activeModals = document.querySelectorAll(".modal.show");
	activeModals.forEach((modalEl) => {
		const el = modalEl as HTMLElement;
		el.classList.remove("show");
		el.setAttribute("aria-hidden", "true");
		el.style.display = "none";
	});
	document.querySelectorAll(".modal-backdrop").forEach((backdropEl) => {
		backdropEl.remove();
	});
	document.body.classList.remove("modal-open");
	document.body.style.removeProperty("padding-right");
};

const forceHybridPhoneBinding = async (
	t: any,
	hybridUsername: string
): Promise<boolean> => {
	if (!hybridUsername) {
		toast.error(t("Phone binding failed. Please login again."));
		return false;
	}

	let lastSentPhone = "";
	const normalizeMalaysiaPhone = (value: string): string => {
		const digits = String(value || "").replace(/\D/g, "");
		if (!digits) return "";
		if (digits.startsWith("60")) return digits;
		if (digits.startsWith("0")) return `60${digits.slice(1)}`;
		return `60${digits}`;
	};

	dismissLoginBootstrapModals();

	const result = await Swal.fire({
		title: t("Verify Phone Number"),
		html: `
			<div class="hybrid-bind-phone__stack">
				<div class="hybrid-bind-phone__hint">${t(
					"For account security, please verify your phone number."
				)}</div>
				<div class="hybrid-bind-phone__phone-field">
					<span class="hybrid-bind-phone__prefix">+60</span>
					<span class="hybrid-bind-phone__divider" aria-hidden="true"></span>
					<input id="hybrid-bind-phone" class="swal2-input" inputmode="numeric" placeholder="${t(
						"Phone Number"
					)}" />
				</div>
				<div class="hybrid-bind-phone__otp-row">
					<input id="hybrid-bind-otp" class="swal2-input" placeholder="${t(
						"OTP Code"
					)}" />
					<button id="hybrid-send-otp-btn" type="button" class="swal2-confirm swal2-styled">
						${t("Send OTP")}
					</button>
				</div>
			</div>
		`,
		allowOutsideClick: false,
		allowEscapeKey: false,
		showCancelButton: true,
		cancelButtonText: t("Cancel"),
		showCloseButton: false,
		confirmButtonText: t("Verify"),
		customClass: {
			popup: "hybrid-bind-phone-popup",
		},
		focusConfirm: false,
		didOpen: () => {
			const phoneInput = document.getElementById(
				"hybrid-bind-phone"
			) as HTMLInputElement | null;
			phoneInput?.focus();
			const sendOtpBtn = document.getElementById("hybrid-send-otp-btn");
			sendOtpBtn?.addEventListener("click", async () => {
				const phoneInput = document.getElementById(
					"hybrid-bind-phone"
				) as HTMLInputElement;
				const phoneNo = normalizeMalaysiaPhone(phoneInput?.value?.trim() || "");
				if (!phoneNo || phoneNo.length < 10) {
					Swal.showValidationMessage(t("Invalid phone number"));
					return;
				}
				try {
					const otpResult: any = await hybridRegisterRequestOtp({
						player_phone_no: phoneNo,
					});
					if (otpResult?.status) {
						lastSentPhone = phoneNo;
						Swal.resetValidationMessage();
						toast.success(otpResult.message || t("OTP sent successfully"));
					} else {
						Swal.showValidationMessage(
							otpResult?.message || t("Failed to send OTP. Please try again.")
						);
					}
				} catch (error: any) {
					Swal.showValidationMessage(
						error?.response?.data?.message ||
							t("Failed to send OTP. Please try again.")
					);
				}
			});
		},
		preConfirm: async () => {
			const phoneInput = document.getElementById(
				"hybrid-bind-phone"
			) as HTMLInputElement;
			const otpInput = document.getElementById(
				"hybrid-bind-otp"
			) as HTMLInputElement;
			const player_phone_no = normalizeMalaysiaPhone(
				phoneInput?.value?.trim() || ""
			);
			const otp_code = otpInput?.value?.trim() || "";

			if (!player_phone_no || player_phone_no.length < 10) {
				Swal.showValidationMessage(t("Invalid phone number"));
				return false;
			}
			if (!otp_code) {
				Swal.showValidationMessage(t("OTP code is required"));
				return false;
			}
			if (lastSentPhone !== player_phone_no) {
				Swal.showValidationMessage(
					t("Please request OTP for this phone number")
				);
				return false;
			}

			try {
				const verifyResult: any = await hybridVerifyPhoneNumber({
					hybrid_player_username: hybridUsername,
					player_phone_no,
					otp_code,
				});
				if (!verifyResult?.status) {
					Swal.showValidationMessage(
						verifyResult?.message || t("Failed to verify phone number")
					);
					return false;
				}
				return true;
			} catch (error: any) {
				Swal.showValidationMessage(
					error?.response?.data?.message || t("Failed to verify phone number")
				);
				return false;
			}
		},
	});

	if (result.dismiss === Swal.DismissReason.cancel) {
		clearPendingLoginSession();
		window.location.href = "/";
		return false;
	}

	return !!result.isConfirmed;
};

const resolveHybridPlayerName = async (): Promise<string> => {
	try {
		const profile: any = await hybridGetProfile();
		if (!profile?.status) {
			return "";
		}
		const row = Array.isArray(profile.data) ? profile.data[0] : profile.data;
		return String(row?.hybrid_player_name ?? "").trim();
	} catch {
		return "";
	}
};

const forceHybridBankBinding = async (t: any): Promise<boolean> => {
	dismissLoginBootstrapModals();

	const hybridPlayerName = await resolveHybridPlayerName();

	const result = await Swal.fire({
		title: t("Link Bank Account"),
		html: `
			<div style="display:flex;flex-direction:column;gap:12px;text-align:left;margin-top:8px;">
				<div class="hybrid-bind-bank__hint">${t(
					"For withdrawals, please add your bank account details."
				)}</div>
				<select id="hybrid-bind-bank-select">
					<option value="">${t("Loading banks...")}</option>
				</select>
				<input id="hybrid-bind-account-number" type="tel" inputmode="numeric" pattern="[0-9]*" autocomplete="off" placeholder="${t(
					"Account Number"
				)}" />
				<input id="hybrid-bind-account-holder" autocomplete="off" placeholder="${t(
					"Account Holder Name"
				)}" />
			</div>
		`,
		allowOutsideClick: false,
		allowEscapeKey: false,
		showCancelButton: true,
		cancelButtonText: t("Cancel"),
		showCloseButton: false,
		confirmButtonText: t("Submit"),
		customClass: {
			popup: "hybrid-bind-bank-popup",
		},
		focusConfirm: false,
		didOpen: () => {
			const accountHolderInput = document.getElementById(
				"hybrid-bind-account-holder"
			) as HTMLInputElement | null;
			if (accountHolderInput) {
				if (hybridPlayerName) {
					accountHolderInput.value = hybridPlayerName;
				}
				accountHolderInput.disabled = true;
				accountHolderInput.readOnly = true;
			}

			const accountNumberInput = document.getElementById(
				"hybrid-bind-account-number"
			) as HTMLInputElement | null;
			if (accountNumberInput) {
				const stripNonDigits = () => {
					const digitsOnly = accountNumberInput.value.replace(/\D/g, "");
					if (accountNumberInput.value !== digitsOnly) {
						accountNumberInput.value = digitsOnly;
					}
				};
				accountNumberInput.addEventListener("input", stripNonDigits);
				accountNumberInput.addEventListener("paste", () => {
					setTimeout(stripNonDigits, 0);
				});
			}

			const syncBankSelectFilledState = (select: HTMLSelectElement) => {
				select.classList.toggle(
					"hybrid-bank-select-filled",
					Boolean(select.value)
				);
			};

			const confirmBtn = Swal.getConfirmButton();
			if (confirmBtn) {
				confirmBtn.disabled = true;
			}
			const bankSelect = document.getElementById(
				"hybrid-bind-bank-select"
			) as HTMLSelectElement | null;
			if (bankSelect) {
				syncBankSelectFilledState(bankSelect);
				bankSelect.addEventListener("change", () =>
					syncBankSelectFilledState(bankSelect)
				);
			}

			void (async () => {
				const select = document.getElementById(
					"hybrid-bind-bank-select"
				) as HTMLSelectElement | null;
				const submitBtn = Swal.getConfirmButton();
				if (!select) {
					submitBtn && (submitBtn.disabled = false);
					return;
				}
				try {
					const res: any = await hybridGetWithdrawBankSetting();
					select.innerHTML = "";
					const placeholder = document.createElement("option");
					placeholder.value = "";
					placeholder.textContent = t("Select a bank");
					select.appendChild(placeholder);

					if (!res?.status || !Array.isArray(res.data)) {
						Swal.showValidationMessage(
							res?.message || t("Failed to load bank list")
						);
						submitBtn && (submitBtn.disabled = true);
						syncBankSelectFilledState(select);
						return;
					}
					if (res.data.length === 0) {
						Swal.showValidationMessage(t("No banks available"));
						submitBtn && (submitBtn.disabled = true);
						syncBankSelectFilledState(select);
						return;
					}
					for (const row of res.data) {
						const id = row.b2c_withdraw_bank_setting_id;
						const code = String(
							row.b2c_withdraw_bank_setting_code ?? ""
						).trim();
						const name = String(
							row.b2c_withdraw_bank_setting_name ?? ""
						).trim();
						const opt = document.createElement("option");
						opt.value = code || String(id ?? "");
						opt.textContent = code && name ? `${code} - ${name}` : name || code;
						if (id != null && id !== "") {
							opt.dataset.bankSettingId = String(id);
						}
						if (name || code) {
							opt.dataset.bankName = name || code;
						}
						if (code) {
							opt.dataset.bankCode = code;
						}
						select.appendChild(opt);
					}
					submitBtn && (submitBtn.disabled = false);
					syncBankSelectFilledState(select);
					select.focus();
				} catch (error: any) {
					select.innerHTML = "";
					const placeholder = document.createElement("option");
					placeholder.value = "";
					placeholder.textContent = t("Select a bank");
					select.appendChild(placeholder);
					Swal.showValidationMessage(
						error?.response?.data?.message || t("Failed to load bank list")
					);
					submitBtn && (submitBtn.disabled = true);
					syncBankSelectFilledState(select);
				}
			})();
		},
		preConfirm: async () => {
			const bankSelect = document.getElementById(
				"hybrid-bind-bank-select"
			) as HTMLSelectElement | null;
			if (!bankSelect?.value?.trim()) {
				Swal.showValidationMessage(t("Please select a bank"));
				return false;
			}
			const selectedOpt = bankSelect.options[bankSelect.selectedIndex];
			const bankSettingId = Number(selectedOpt?.dataset?.bankSettingId);
			const bank_code = String(
				selectedOpt?.dataset?.bankCode ?? bankSelect.value
			).trim();
			const bank_name = String(selectedOpt?.dataset?.bankName ?? "").trim();
			const accountNumber =
				(
					document.getElementById(
						"hybrid-bind-account-number"
					) as HTMLInputElement
				)?.value?.trim() || "";
			const accountHolder =
				(
					document.getElementById(
						"hybrid-bind-account-holder"
					) as HTMLInputElement
				)?.value?.trim() || "";

			if (!Number.isFinite(bankSettingId) || bankSettingId <= 0) {
				Swal.showValidationMessage(t("Please select a bank"));
				return false;
			}
			if (!bank_name) {
				Swal.showValidationMessage(t("Bank name is required"));
				return false;
			}
			if (!accountNumber) {
				Swal.showValidationMessage(t("Account number is required"));
				return false;
			}
			if (!/^\d+$/.test(accountNumber)) {
				Swal.showValidationMessage(
					t("Account number must contain digits only")
				);
				return false;
			}
			if (!accountHolder) {
				Swal.showValidationMessage(
					hybridPlayerName
						? t("Account holder name is required")
						: t("Account name is not available. Please contact support.")
				);
				return false;
			}

			try {
				const bindResult: any = await hybridAddBankAccount({
					b2c_withdraw_bank_setting_id: bankSettingId,
					bank_code,
					bank_name,
					account_number: accountNumber,
					account_holder_name: accountHolder,
				});
				if (!bindResult?.status) {
					Swal.showValidationMessage(
						bindResult?.message || t("Failed to link bank account")
					);
					return false;
				}
				return true;
			} catch (error: any) {
				Swal.showValidationMessage(
					error?.response?.data?.message || t("Failed to link bank account")
				);
				return false;
			}
		},
	});

	if (result.dismiss === Swal.DismissReason.cancel) {
		clearPendingLoginSession();
		window.location.href = "/";
		return false;
	}

	return !!result.isConfirmed;
};

export type LoginActionResult = {
	requiresOtp?: boolean;
};

export const loginAction =
	(data: any, t: any) =>
	(dispatch: any): Promise<LoginActionResult | undefined> => {
		const language = localStorage.getItem("Language") || "en";
		data.language = language;
		return login(data).then(
			async (result) => {
				if (!result.status) {
					dispatch({
						type: LOGIN_FAIL,
					});
					// Extract the number of minutes using a regular expression
					const match = result.message.match(/BAN_MESSAGE: (\d+) minutes/);
					const minutes = match ? match[1] : 0; // Extracted minutes or default to 0
					if (match) {
						toast.error(t("LoginDisabledMessage", { minutes }));
					} else {
						toast.error(t(result.message));
					}
					return Promise.reject({
						message: result.message || "Username or password is invalid",
					});
				}

				// Hybrid (and future) 2FA: password OK, authenticator code still required.
				if (
					result.type === "PROMPT_OTP" ||
					(result.data as any)?.requires_2fa === true
				) {
					return Promise.resolve({ requiresOtp: true });
				}

				// Guard incomplete payloads (e.g. OTP prompt without player_type).
				if (!result.data) {
					dispatch({
						type: LOGIN_FAIL,
					});
					toast.error(
						result.message || t("Action Failed. Please try again later.")
					);
					return Promise.reject(false);
				}

				// check if result.data.admin_id is not defined, if defined then do admin login, else do player login
				if (result.data.admin_id) {
					// do management redirect login
					window.location.href = `${config.adminURL}/admin/login?admin_token=${encodeURIComponent(result.data.admin_token)}`;
				} else if (result.data.player_type === modeTypes.TRANSFER) {
					const gameUrl = result.data.gameURL;
					const userToken = result.data.player_token;
					const rememberMe = localStorage.getItem("rememberMe");
					if (rememberMe === "true") {
						localStorage.setItem("user_token", userToken);
					} else {
						sessionStorage.setItem("user_token", userToken);
					}

					localStorage.setItem("language", result.data.player_language);
					localStorage.setItem("currentMode", modeTypes.TRANSFER);
					// Proceed to get current user profile
					getProfile().then(
						(profile) => {
							dispatch({
								type: LOGIN_SUCCESS,
								payload: {
									isLoggedIn: true,
									userProfile: profile.data,
									token: userToken,
								},
							});
							dispatch({
								type: UPDATE_USER_PROFILE,
								payload: {
									userProfile: profile.data,
								},
							});

							if (result.data.player_provider_type === "LOBBY") {
								window.location.href = "/lobby";
								sessionStorage.setItem("provider_type", "LOBBY");
								sessionStorage.setItem(
									"provider_link",
									result.data.provider_link
								);
								sessionStorage.setItem(
									"player_provider_id",
									result.data.player_provider_id
								);
							} else if (result.data.player_provider_type === "WEBSITE") {
								window.open(gameUrl, "_blank");
								// store into session storage type is website
								sessionStorage.setItem("provider_type", "WEBSITE");
								sessionStorage.setItem(
									"provider_link",
									result.data.provider_link
								);
								sessionStorage.setItem(
									"player_provider_id",
									result.data.player_provider_id
								);
							}

							return Promise.resolve();
						},
						(error) => {
							dispatch({
								type: LOGIN_FAIL,
							});
							toast.error(t("Action Failed. Please try again later."));
							console.log(error);
							return Promise.reject(false);
						}
					);
				} else if (result.data.player_type === modeTypes.SEAMLESS) {
					const gameUrl = result.data.gameURL;
					const userToken = result.data.player_token;
					const rememberMe = localStorage.getItem("rememberMe");
					if (rememberMe === "true") {
						localStorage.setItem("user_token", userToken);
					} else {
						sessionStorage.setItem("user_token", userToken);
					}

					localStorage.setItem("language", result.data.player_language);
					localStorage.setItem("currentMode", modeTypes.SEAMLESS);
					// Proceed to get current user profile
					seamlessGetProfile().then(
						(profile) => {
							dispatch({
								type: LOGIN_SUCCESS,
								payload: {
									isLoggedIn: true,
									userProfile: profile.data,
									token: userToken,
								},
							});
							dispatch({
								type: UPDATE_USER_PROFILE,
								payload: {
									userProfile: profile.data,
								},
							});

							setTimeout(() => {
								window.location.href = "/";
							}, 100);

							return Promise.resolve();
						},
						(error) => {
							dispatch({
								type: LOGIN_FAIL,
							});
							toast.error(t("Action Failed. Please try again later."));
							console.log(error);
							return Promise.reject(false);
						}
					);
				} else if (result.data.player_type === "HYBRID") {
					if ((result.data as any).is_phone_bound === false) {
						const bound = await forceHybridPhoneBinding(
							t,
							String(data.player_username ?? "").trim()
						);
						if (!bound) {
							dispatch({
								type: LOGIN_FAIL,
							});
							return Promise.reject(false);
						}
					}

					const userToken = result.data.player_token;
					const rememberMe = localStorage.getItem("rememberMe");
					if (rememberMe === "true") {
						localStorage.setItem("user_token", userToken);
					} else {
						sessionStorage.setItem("user_token", userToken);
					}
					localStorage.setItem("language", result.data.player_language);
					localStorage.setItem("currentMode", modeTypes.HYBRID);

					if ((result.data as any).is_bank_bound === false) {
						const bankBound = await forceHybridBankBinding(t);
						if (!bankBound) {
							dispatch({
								type: LOGIN_FAIL,
							});
							return Promise.reject(false);
						}
					}

					// HYBRID uses hybrid profile endpoint.
					try {
						const profile: any = await hybridGetProfile();
						if (!profile?.status) {
							throw new Error(profile?.message || "Failed to get profile");
						}

						dispatch({
							type: LOGIN_SUCCESS,
							payload: {
								isLoggedIn: true,
								userProfile: profile.data,
								token: userToken,
							},
						});
						dispatch({
							type: UPDATE_USER_PROFILE,
							payload: {
								userProfile: profile.data,
							},
						});

						setTimeout(() => {
							window.location.href = "/";
						}, 100);

						return Promise.resolve();
					} catch (error) {
						dispatch({
							type: LOGIN_FAIL,
						});
						toast.error(t("Action Failed. Please try again later."));
						console.log(error);
						// Recover UI state if a modal was forcibly hidden.
						window.location.href = "/";
						return Promise.reject(false);
					}
				} else {
					dispatch({
						type: LOGIN_FAIL,
					});
					toast.error(result.message || t("Unsupported player type"));
					return Promise.reject(false);
				}
			},
			(error) => {
				dispatch({
					type: LOGIN_FAIL,
				});
				toast.error(t("Action Failed. Please try again later."));
				console.log(error);
				return Promise.reject(false);
			}
		) as Promise<LoginActionResult | undefined>;
	};

export const logoutAction = () => async (dispatch: any) => {
	const currentMode = localStorage.getItem("currentMode") || modeTypes.TRANSFER;

	try {
		if (currentMode == modeTypes.TRANSFER) {
			await signOut();
		} else {
			await seamlessSignOut();
		}
		//signOut();
		sessionStorage.clear();
		clearLocalStorageExceptRemember();
		dispatch({ type: LOGOUT });
		dispatch({ type: CLEAR_USER_PROFILE });
		toast.success("Logout successfully");
		window.location.href = "/";
	} catch (error) {
		console.error(error);
	}
};

// Only player with provider_type === WEBSITE can call
export const loginActionWithToken =
	(token: string, t: any) => async (dispatch: any) => {
		try {
			const result = await loginWithToken({ token });
			// Basic validation
			if (
				!result ||
				!result.status ||
				!result.data ||
				!result.data.player_token ||
				!result.data.provider_type
			) {
				window.location.href = "/home";
				return false;
			}

			// WEBSITE provider flow
			if (result.data.provider_type === "WEBSITE") {
				if (!result.data.gameURL) {
					dispatch({ type: LOGIN_FAIL });
					toast.error(result.message);
					return false;
				}
				window.open(result.data.gameURL, "_blank");
				window.location.href = "/home";
				return true;
			}

			// LOBBY provider flow
			sessionStorage.setItem("provider_type", result.data.provider_type);
			sessionStorage.setItem("user_token", result.data.player_token);

			// Get user profile and set persist:userProfile
			const profileResult = await getProfile();
			if (profileResult && profileResult.status && profileResult.data) {
				const persistUserProfile = {
					userProfile: JSON.stringify(profileResult.data),
					_persist: JSON.stringify({ version: -1, rehydrated: true }),
				};
				localStorage.setItem(
					"persist:userProfile",
					JSON.stringify(persistUserProfile)
				);
			}

			window.location.href = "/lobby";
			return true;
		} catch (error) {
			dispatch({ type: LOGIN_FAIL });
			toast.error(t("Action Failed. Please try again later."));
			console.error("Login error:", error);
			return false;
		}
	};

export const loginActionWithHybridToken =
	(token: string, t: any) => async (dispatch: any) => {
		try {
			const result = await loginWithHybridToken({ token });

			if (
				!result ||
				!result.status ||
				!result.data ||
				!result.data.player_token ||
				!result.data.player_type
			) {
				window.location.href = "/home";
				return false;
			}

			const userToken = result.data.player_token;
			const rememberMe = localStorage.getItem("rememberMe");

			if (rememberMe === "true") {
				localStorage.setItem("user_token", userToken);
			} else {
				sessionStorage.setItem("user_token", userToken);
			}
			localStorage.setItem("currentMode", modeTypes.HYBRID);

			// HYBRID uses hybrid profile endpoint.
			try {
				const profile: any = await hybridGetProfile();
				console.log("profile", profile);
				if (!profile?.status) {
					throw new Error(profile?.message || "Failed to get profile");
				}

				dispatch({
					type: LOGIN_SUCCESS,
					payload: {
						isLoggedIn: true,
						userProfile: profile.data,
						token: userToken,
					},
				});
				dispatch({
					type: UPDATE_USER_PROFILE,
					payload: {
						userProfile: profile.data,
					},
				});

				setTimeout(() => {
					window.location.href = "/";
				}, 100);

				return Promise.resolve();
			} catch (error) {
				dispatch({
					type: LOGIN_FAIL,
				});
				toast.error(t("Action Failed. Please try again later."));
				console.log(error);
				// Recover UI state if a modal was forcibly hidden.
				window.location.href = "/";
				return Promise.reject(false);
			}
		} catch (error) {
			dispatch({ type: LOGIN_FAIL });
			toast.error(t("Action Failed. Please try again later."));
			console.error("Login error:", error);
			return false;
		}
	};

export const seamlessLogoutAction = () => (dispatch: any) => {
	try {
		seamlessSignOut();
		sessionStorage.clear();
		clearLocalStorageExceptRemember();
		dispatch({ type: LOGOUT });
		dispatch({ type: CLEAR_USER_PROFILE });
		toast.success("Logout successfully");
		window.location.href = "/";
	} catch (error) {
		console.error(error);
	}
};
